// Learning Framework Builder - concept data layer.
//
// A self-serve version of the offline "Learning Framework" exercise: the admin
// answers a short questionnaire and Elliott recommends the Training / Role
// Excellence / Development / Standards & Compliance programs their org should
// run, rendered like the Learning Framework dashboard.
//
// FE concept: the catalog + rules live here; nothing is persisted server-side.
// "Template-ready" programs are the ones we already have (or are building) as
// reusable templates, so they can be added instantly; the rest are custom builds.

// Dashboard parity - same pillar columns and program-type colours as the
// Learning Framework dashboard (see learning-lifecycle/constants + PillarColumn).
export type FwPillarKey = 'onboard' | 'operate' | 'perform' | 'lead';
export type FwTypeKey = 'role-excellence' | 'development' | 'standards';

export const FW_PILLARS: { key: FwPillarKey; label: string }[] = [
  { key: 'onboard', label: 'ONBOARD' },
  { key: 'operate', label: 'OPERATE' },
  { key: 'perform', label: 'PERFORM' },
  { key: 'lead', label: 'LEAD' }
];

// Matches PROGRAM_TYPE_COLORS on the dashboard.
export const FW_TYPE_COLORS: Record<FwTypeKey, string> = {
  'role-excellence': '#F313BF', // pink
  development: '#009BF1', // blue
  standards: '#A012FF' // purple
};
export const FW_TYPE_LABELS: Record<FwTypeKey, string> = {
  'role-excellence': 'Role Excellence',
  development: 'Development',
  standards: 'Standards & Compliance'
};

// Program format. Assessments aren't available yet, so every recommendation is a
// course. A framework saved before that change can still hold assessments
// (per-team Role Excellence, the Personal Development baseline); isAssessment
// finds them so the results page leaves them out.
export type FwFormatKey = 'assessment' | 'course';
export const isAssessment = (p: { name: string; format?: FwFormatKey }): boolean =>
  p.format === 'assessment' || /\bassessment\s*$/i.test((p.name || '').trim());

// The programs we already have pre-built as reusable templates ("Ready-built").
// Everything else is a custom build. This allow-list is the single source of
// truth for the readyBuilt flag and the "always include one ready-built" rule.
// The Ready-built badge itself is hidden until ready-built programs ship.
export const READY_BUILT_IDS = new Set<string>([
  'info-security',
  'workplace-conduct',
  'responsible-ai-use',
  'ai-foundations',
  'ai-in-your-workday'
]);
// A live (BE) program carries readyBuilt decided by the server; engine output
// falls back to the allow-list.
export const isReadyBuilt = (program: string | { id: string; readyBuilt?: boolean }): boolean =>
  typeof program === 'string' ? READY_BUILT_IDS.has(program) : program.readyBuilt ?? READY_BUILT_IDS.has(program.id);

// The teams the org can select in the opening question. Each selected team gets
// its own Role Excellence course. Values that overlap the AI-function keys
// (sales / finance / engineering / ...) still power the AI-by-function rules.
export const TEAM_OPTIONS: { value: string; label: string }[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'customer', label: 'Customer Support' },
  { value: 'customer-success', label: 'Customer Success' },
  { value: 'product', label: 'Product' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'operations', label: 'Operations' },
  { value: 'finance', label: 'Finance' },
  { value: 'hr', label: 'People / HR' },
  { value: 'legal', label: 'Legal' },
  { value: 'it', label: 'IT' },
  { value: 'data', label: 'Data & Analytics' },
  { value: 'design', label: 'Design' }
];
export const teamLabel = (value: string): string =>
  TEAM_OPTIONS.find((t) => t.value === value)?.label ||
  value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export interface FwProgram {
  id: string;
  name: string;
  description: string; // short, card-friendly
  pillar: FwPillarKey;
  type: FwTypeKey;
  // Always 'course' for new recommendations (see isAssessment).
  format?: FwFormatKey;
  // We already have (or are building) a reusable template → instant add.
  templateReady: boolean;
  // Free-form tags the recommendation rules match against.
  tags: string[];
  // Optional: only recommend for these functions (Q6). Empty = any.
  functions?: FwFunctionKey[];
  // Optional: only recommend when this country is selected (Q2).
  country?: 'uk' | 'us';
  // ── Recommendation fields ──
  // Server-decided Ready-built flag (see isReadyBuilt); the badge is hidden for now.
  readyBuilt?: boolean;
  // Why this company needs it, in full sentences that cite what the admin told
  // Elliott (countries, industry, teams, headcount, priorities...).
  basis?: string;
  // Compliance item the company should confirm with its legal team.
  needsLegalReview?: boolean;
}

export type FwFunctionKey =
  | 'operations'
  | 'sales'
  | 'customer'
  | 'finance'
  | 'engineering'
  | 'marketing'
  | 'hr';

// ── Catalog ──────────────────────────────────────────────────────────────────
// `templateReady: true` = from the Standards & Compliance + AI Adoption sets the
// team is building. The Onboarding / Role Excellence / Management & Leadership /
// Development entries round the framework out and are custom builds for now.

export const FW_CATALOG: FwProgram[] = [
  // ── Onboarding (role-excellence) ──
  { id: 'company-onboarding', name: 'Welcome to the company', description: "What every new hire needs in their first weeks: the mission and values, how work gets done, who's who, the tools they will use, and what good looks like in their first 90 days.", pillar: 'onboard', type: 'role-excellence', templateReady: false, tags: ['core', 'onboarding'] },
  { id: 'role-onboarding', name: 'Getting up to speed in your role', description: "A structured first 90 days for each role: the team's goals, the systems and processes the role relies on, the people to know, and the milestones that show a new hire is fully up to speed.", pillar: 'onboard', type: 'role-excellence', templateReady: false, tags: ['onboarding'] },

  // ── Role Excellence ──
  { id: 'product-knowledge', name: 'Knowing the product inside out', description: 'What the product does, who it is for and the problems it solves, the features customers use most, and how to explain its value in plain language.', pillar: 'operate', type: 'role-excellence', templateReady: false, tags: ['role', 'product'] },
  { id: 'role-skills', name: 'Mastering the tools of the job', description: 'The tools, systems and working methods each team relies on day to day, with practical walkthroughs of the tasks people do most.', pillar: 'operate', type: 'role-excellence', templateReady: false, tags: ['role'] },
  { id: 'customer-service', name: 'Delivering outstanding customer service', description: 'Handling customers consistently and with empathy: active listening, clear written replies, calming a frustrated customer, and knowing when and how to escalate.', pillar: 'operate', type: 'role-excellence', templateReady: false, tags: ['role'] },
  { id: 'industry-fundamentals', name: 'Understanding the industry', description: 'The market the business operates in: how it makes money, who the competitors are, the trends shaping it, and the customer pain points behind the work.', pillar: 'operate', type: 'role-excellence', templateReady: false, tags: ['industry'] },

  // ── Management & Leadership (development) ──
  { id: 'first-time-manager', name: 'Stepping up to management', description: 'The core skills a brand-new manager needs in their first year: coaching and 1:1s, delegating without micromanaging, setting expectations, giving feedback, and building a team that performs when they are not in the room.', pillar: 'lead', type: 'development', templateReady: false, tags: ['manager'] },
  { id: 'management-essentials', name: 'Leading teams that perform', description: 'The fundamentals every people manager relies on: setting direction and goals, running effective 1:1s and team meetings, managing performance fairly, and making decisions with confidence so managers lead consistently across the business.', pillar: 'lead', type: 'development', templateReady: false, tags: ['manager'] },
  { id: 'leadership-development', name: 'Leading through growth and change', description: 'Developing senior and emerging leaders to set direction, shape culture and grow teams: strategic thinking, leading through change, developing other leaders, and making sound decisions with incomplete information.', pillar: 'lead', type: 'development', templateReady: false, tags: ['leadership', 'senior'] },

  // ── Development ──
  { id: 'mentorship', name: 'Growing through mentorship', description: 'A structured mentoring program that pairs people to pass on knowledge and accelerate growth: how to match mentors and mentees, run useful sessions, set goals and keep the relationship on track.', pillar: 'perform', type: 'development', templateReady: false, tags: ['mentorship', 'intern'] },
  { id: 'early-careers', name: 'Launching early careers', description: 'A structured early-careers path that ramps graduates and interns quickly: focused onboarding, mentoring and regular check-ins, plus the workplace and role skills they need to contribute with confidence and stay.', pillar: 'perform', type: 'development', templateReady: false, tags: ['intern'] },
  { id: 'career-development', name: 'Building careers people stay for', description: 'Helping people grow: clear development plans, honest career conversations, and regular performance check-ins that connect day-to-day work to where someone wants to go.', pillar: 'perform', type: 'development', templateReady: false, tags: ['career', 'turnover'] },
  {
    id: 'feedback-performance',
    name: 'Giving feedback that lands',
    description:
      'Practical frameworks for giving clear, specific, kind feedback and running 1:1s people act on: preparing for the harder conversations, separating behavior from personality, and agreeing next steps that actually stick.',
    pillar: 'perform',
    type: 'development',
    templateReady: false,
    tags: ['feedback', 'turnover']
  },
  { id: 'comms-collaboration', name: 'Working better across teams', description: 'How your teams communicate and work well together: clear written updates, useful meetings, async norms, and collaborating across teams, offices and time zones so less gets lost or duplicated between people.', pillar: 'perform', type: 'development', templateReady: false, tags: ['remote', 'multisite'] },

  // ── Standards & Compliance (standards) - template-ready ──
  {
    id: 'info-security',
    name: 'Spotting and stopping cyber threats',
    description:
      'How to recognize and respond to phishing and social engineering, and the everyday habits that keep data safe: strong passwords and MFA, safe device use, and spotting the mistakes that cause most breaches.',
    pillar: 'onboard',
    type: 'standards',
    templateReady: true,
    tags: ['core', 'security']
  },
  {
    id: 'workplace-conduct',
    name: 'Building a respectful workplace',
    description:
      'The behavior everyone is expected to uphold: respect, anti-harassment and anti-discrimination, appropriate conduct in person and online, and exactly how to raise or report a concern when something falls short.',
    pillar: 'onboard',
    type: 'standards',
    templateReady: true,
    tags: ['core', 'conduct']
  },
  { id: 'data-protection', name: 'Handling personal data with care', description: 'How to collect, store, share and dispose of personal and confidential data safely in everyday work, the rights people have over their data, and what to do the moment something is lost or exposed.', pillar: 'onboard', type: 'standards', templateReady: true, tags: ['core', 'data'] },
  {
    id: 'responsible-ai-use',
    name: 'Using AI safely at work',
    description:
      'Practical judgment for using AI tools safely at work: what you must never paste into them, how to check outputs for accuracy and bias, protecting confidential and customer data, and where a human must stay in the loop.',
    pillar: 'onboard',
    type: 'standards',
    templateReady: true,
    tags: ['core', 'ai']
  },
  { id: 'financial-controls', name: 'Getting financial controls right', description: 'The everyday controls that protect the business: expenses and purchasing rules, approval thresholds, separation of duties, and how to spot and escalate anything that looks like fraud or error.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['finance'] },
  { id: 'confidentiality', name: 'Keeping confidential information safe', description: 'Handling confidential company and client information properly: what counts as confidential, NDAs in practice, secure sharing and storage, and avoiding the accidental leaks that do real damage.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['data', 'incident'] },
  { id: 'anti-bribery-uk', name: 'Preventing bribery and corruption (UK)', description: 'What bribery and corruption look like in practice, your obligations under the UK Bribery Act 2010, and how to handle gifts, hospitality and third parties without crossing the line.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['bribery'], country: 'uk' },
  { id: 'anti-bribery-us', name: 'Preventing bribery and corruption (US)', description: 'Bribery and corruption risks in a US and international context, obligations under the FCPA, and how to handle gifts, hospitality, facilitation payments and third parties safely.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['bribery'], country: 'us' },
  { id: 'conflicts-of-interest', name: 'Managing conflicts of interest', description: 'How to spot, disclose and manage situations where personal interests could clash with the company: outside roles, relationships, gifts and investments, so decisions stay fair and defensible.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['ethics'] },
  { id: 'business-ethics', name: 'Doing business with integrity', description: 'A shared framework for acting with integrity: gifts and hospitality, dealing with third parties and competitors, honest communication, and how to make the right call when the answer is not obvious.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['ethics'] },
  { id: 'ip-protection', name: 'Protecting intellectual property', description: 'What counts as company intellectual property and the practical habits that protect it: confidentiality, secure handling, and the new risks that come from pasting work into public AI tools.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['ip'] },
  { id: 'social-media', name: 'Representing the company online', description: 'How to represent the company well online and in public: what can and cannot be shared externally, handling comments and mistakes, and keeping personal and company voices appropriately separate.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['comms'] },
  { id: 'whistleblowing', name: 'Speaking up with confidence', description: 'What a reportable concern is, the channels to raise one confidentially, the protections in place for people who speak up, and why a strong speaking-up culture protects everyone.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['culture', 'incident'] },
  {
    id: 'risk-awareness',
    name: 'Spotting and escalating risk early',
    description:
      'Recognizing operational, financial, safety and reputational risks in everyday work, and the habit of escalating early through the right channel so small issues never become big ones.',
    pillar: 'operate',
    type: 'standards',
    templateReady: true,
    tags: ['risk', 'incident']
  },
  {
    id: 'records-management',
    name: 'Managing documents and records well',
    description:
      'How to create, name, store, retain and dispose of documents and records correctly, so the business stays organized, compliant and audit-ready and nothing important is ever lost.',
    pillar: 'operate',
    type: 'standards',
    templateReady: true,
    tags: ['records', 'incident']
  },
  { id: 'supplier-standards', name: 'Working responsibly with suppliers', description: 'What is expected when selecting and working with vendors and partners: due diligence, contracts and data-sharing, and holding third parties to the same standards you hold yourselves.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['supplier'] },
  { id: 'business-continuity', name: 'Keeping the business running through disruption', description: 'Everyone’s role when something disrupts normal operations, from an outage to a crisis: who to contact, how to keep critical work going, and how to recover quickly and calmly.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['continuity', 'ma'] },
  { id: 'sustainability-esg', name: 'Making sustainability part of the job', description: 'The company’s environmental, social and governance commitments and the practical steps every person can take in their role to help meet them and report honestly on progress.', pillar: 'operate', type: 'standards', templateReady: true, tags: ['esg'] },
  { id: 'code-of-conduct', name: 'Putting the code of conduct into practice', description: 'The company’s core standards for how everyone is expected to behave and make decisions, how the code applies to real situations, and how to raise concerns the right way.', pillar: 'onboard', type: 'standards', templateReady: true, tags: ['conduct', 'ma'] },

  // ── AI Adoption (development) - template-ready ──
  { id: 'ai-foundations', name: 'Getting started with AI', description: 'A confident starting point for everyone: what AI and large language models actually are, how they generate answers, why they sometimes get things wrong, and how to start using them well and safely at work.', pillar: 'operate', type: 'development', templateReady: true, tags: ['core', 'ai'] },
  {
    id: 'ai-in-your-workday',
    name: 'Building AI into your workday',
    description:
      'The practical, everyday wins from AI: drafting and editing, summarizing long documents and threads, quick research, meeting prep and follow-ups, so people save real time on the work they already do.',
    pillar: 'perform',
    type: 'development',
    templateReady: true,
    tags: ['core', 'ai']
  },
  { id: 'first-ai-tools', name: 'Choosing the right AI tools', description: 'A guided tour of the main AI tools and which one to reach for, with hands-on examples of what each does well so people can start with confidence instead of guesswork.', pillar: 'operate', type: 'development', templateReady: true, tags: ['ai'] },
  { id: 'prompting', name: 'Writing prompts that get results', description: 'How to write prompts that get useful, reliable results: giving clear context and examples, being specific about the format you want, and refining follow-ups so AI does what you actually need.', pillar: 'perform', type: 'development', templateReady: true, tags: ['ai', 'transformation'] },
  { id: 'advanced-ai', name: 'Unlocking advanced AI', description: 'Going beyond the basics with deep research, custom GPTs, file and data analysis, and AI agents, so people can take on complex, multi-step work and get far more out of the tools they already have.', pillar: 'perform', type: 'development', templateReady: true, tags: ['ai', 'transformation'] },
  { id: 'ai-for-managers', name: 'Leading with AI as a manager', description: 'How managers can use AI to cut admin, make sense of information quickly, prepare for conversations, and support their teams, while setting sensible expectations for how their people use it too.', pillar: 'lead', type: 'development', templateReady: true, tags: ['ai', 'manager'] },
  {
    id: 'ai-in-sales',
    name: 'Leveraging AI in Sales',
    description:
      'How sales teams use AI across the cycle: researching accounts and prospects, personalizing outreach, preparing for meetings, handling follow-ups, and keeping the pipeline moving faster.',
    pillar: 'operate',
    type: 'development',
    templateReady: true,
    tags: ['ai'],
    functions: ['sales']
  },
  { id: 'ai-in-marketing', name: 'Leveraging AI in Marketing', description: 'How marketing teams use AI for research and ideation, drafting and editing content, planning campaigns, and repurposing what works across channels, without losing the brand voice.', pillar: 'operate', type: 'development', templateReady: true, tags: ['ai'], functions: ['marketing'] },
  { id: 'ai-in-operations', name: 'Leveraging AI in Operations', description: 'How operations teams use AI to streamline and document processes, cut repetitive admin, draft standard procedures, and make everyday workflows faster and more consistent.', pillar: 'operate', type: 'development', templateReady: true, tags: ['ai'], functions: ['operations'] },
  { id: 'ai-in-finance', name: 'Leveraging AI in Finance', description: 'How finance teams use AI for analysis and reporting, forecasting and scenario work, and routine financial workflows, with the checks needed to keep numbers accurate and trustworthy.', pillar: 'operate', type: 'development', templateReady: true, tags: ['ai'], functions: ['finance'] },
  { id: 'ai-in-hr', name: 'Leveraging AI in HR', description: 'How people teams use AI across recruitment, onboarding, internal communications, learning, and everyday people operations, while handling sensitive employee data responsibly.', pillar: 'operate', type: 'development', templateReady: true, tags: ['ai'], functions: ['hr'] },
  { id: 'ai-for-developers', name: 'Leveraging AI in Engineering', description: 'How engineers use AI coding tools to accelerate development, from generating and reviewing code to debugging and documentation, while keeping quality, security, and human oversight intact.', pillar: 'operate', type: 'development', templateReady: true, tags: ['ai'], functions: ['engineering'] }
];

// ── Questionnaire ────────────────────────────────────────────────────────────
// Kept deliberately short (< 5 min) and mostly single/multi-select. Free text is
// only used where it adds real value the website can't (the URL itself).

export type QuestionKind = 'single' | 'multi' | 'number' | 'text' | 'grouped-multi';

export interface QuestionOption {
  value: string;
  label: string;
}
export interface QuestionGroup {
  label: string;
  options: QuestionOption[];
}
export interface FwQuestion {
  id: string;
  title: string;
  helper?: string;
  kind: QuestionKind;
  options?: QuestionOption[];
  groups?: QuestionGroup[];
  allowOther?: boolean;
  placeholder?: string;
  optional?: boolean;
}

export const FW_QUESTIONS: FwQuestion[] = [
  {
    id: 'website',
    title: "What's your company website?",
    kind: 'text',
    placeholder: 'e.g. dosen.io'
  },
  {
    id: 'industry',
    title: 'What industry are you in?',
    kind: 'single',
    allowOther: true,
    options: [
      { value: 'hospitality', label: 'Hospitality, Food & Beverage' },
      { value: 'retail', label: 'Retail' },
      { value: 'technology', label: 'Technology & Software' },
      { value: 'healthcare', label: 'Healthcare & Life Sciences' },
      { value: 'financial', label: 'Financial Services' },
      { value: 'professional', label: 'Professional Services' },
      { value: 'construction', label: 'Construction & Trades' },
      { value: 'manufacturing', label: 'Manufacturing & Logistics' }
    ]
  },
  {
    id: 'countries',
    title: 'In which countries do you employ people or operate?',
    kind: 'multi',
    allowOther: true,
    options: [
      { value: 'us', label: 'United States' },
      { value: 'uk', label: 'United Kingdom' },
      { value: 'ca', label: 'Canada' },
      { value: 'ie', label: 'Ireland' },
      { value: 'au', label: 'Australia' },
      { value: 'de', label: 'Germany' },
      { value: 'fr', label: 'France' }
    ]
  },
  {
    id: 'functions',
    title: 'Which teams exist in your company?',
    kind: 'multi',
    allowOther: true,
    options: TEAM_OPTIONS
  },
  {
    id: 'employees',
    title: 'How many employees do you have?',
    kind: 'number',
    placeholder: 'e.g. 150'
  },
  {
    id: 'managers',
    title: 'How many of your employees are people managers?',
    kind: 'number',
    placeholder: 'e.g. 12'
  },
  {
    id: 'activities',
    title: 'Which of these does your business do?',
    kind: 'multi',
    allowOther: true,
    options: [
      { value: 'personal-data', label: 'Handle customer or employee personal data' },
      { value: 'health-data', label: 'Handle health or medical information' },
      { value: 'children-data', label: "Handle children's or under-18s' data" },
      { value: 'payments', label: 'Take payments or handle financial transactions' },
      { value: 'financial-advice', label: 'Provide financial products or advice' },
      { value: 'safety-critical', label: 'Do physically demanding or safety-critical work' },
      { value: 'government', label: 'Sell to government or the public sector' },
      { value: 'listed', label: 'Are a publicly listed company' },
      { value: 'union', label: 'Have a unionised workforce' },
      { value: 'none', label: 'None of these' }
    ]
  },
  {
    id: 'currentPrograms',
    title: 'Which of these do you already run?',
    kind: 'grouped-multi',
    allowOther: true,
    groups: [
      { label: 'Onboarding', options: [
        { value: 'company-onboarding', label: 'Company onboarding & welcome' },
        { value: 'role-onboarding', label: 'Role-specific onboarding' }
      ] },
      { label: 'Role Excellence', options: [
        { value: 'role-skills', label: 'Role-specific skills training' },
        { value: 'product-knowledge', label: 'Product knowledge training' },
        { value: 'customer-service', label: 'Customer service training' }
      ] },
      { label: 'Management & Leadership', options: [
        { value: 'first-time-manager', label: 'First-time manager training' },
        { value: 'management-essentials', label: 'Management essentials' },
        { value: 'leadership-development', label: 'Leadership development' }
      ] },
      { label: 'Standards & Compliance', options: [
        { value: 'workplace-conduct', label: 'Anti-harassment & workplace conduct' },
        { value: 'data-protection', label: 'Data protection & privacy' },
        { value: 'info-security', label: 'Information security & cybersecurity' },
        { value: 'code-of-conduct', label: 'Code of conduct & ethics' }
      ] },
      { label: 'Development', options: [
        { value: 'mentorship', label: 'Mentorship program' },
        { value: 'career-development', label: 'Career development & performance' },
        { value: 'ai-foundations', label: 'AI & technology upskilling' },
        { value: 'comms-collaboration', label: 'Communication & collaboration' }
      ] }
    ]
  },
  {
    id: 'priorities',
    title: 'What are your top priorities for the next 6 to 12 months?',
    helper: 'Pick the ones that matter most.',
    kind: 'multi',
    allowOther: true,
    options: [
      { value: 'onboarding', label: 'Onboard new hires faster' },
      { value: 'retention', label: 'Reduce turnover and improve retention' },
      { value: 'leadership', label: 'Build management and leadership' },
      { value: 'performance', label: 'Improve team and role performance' },
      { value: 'compliance', label: 'Meet compliance and risk obligations' },
      { value: 'ai', label: 'Upskill our people on AI' },
      { value: 'standardise', label: 'Standardize how work gets done' }
    ]
  },
  {
    id: 'hireNext',
    title: 'How many do you plan to hire in the next 12 months?',
    kind: 'single',
    options: [
      { value: 'lt5', label: 'Fewer than 5' },
      { value: '5-15', label: '5 to 15' },
      { value: '16-30', label: '16 to 30' },
      { value: '31-50', label: '31 to 50' },
      { value: '50+', label: '50+' }
    ]
  },
  {
    id: 'aiCapability',
    title: 'How would you describe your team’s AI capability today?',
    kind: 'single',
    options: [
      { value: 'none', label: 'Just getting started (little or no AI use)' },
      { value: 'basic', label: 'Some people use AI informally' },
      { value: 'adopting', label: 'Actively adopting AI across teams' },
      { value: 'advanced', label: 'Advanced, with AI embedded in workflows' }
    ]
  }
];

// Employee-count midpoints used by the ROI model.
export const SIZE_MIDPOINT: Record<string, number> = {
  lt50: 35,
  '50-100': 75,
  '101-250': 175,
  '251-500': 375,
  '1000+': 1200
};
